/* eslint-disable camelcase */
import { useMemo } from 'react'
import classNames from 'classnames'
import { SiteCodes } from 'shared-types'
import Status from '~/components/Status/Status'

import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'

export type HelperTextProps = {
  siteCode?: string
  availableForDelivery?: string
  inStockText?: string
  inStore?: boolean
  inStock?: boolean
  isEndlessAisleFromSupplier?: boolean
  isEndlessAisleOnHand?: boolean
  hasSelectedStore?: boolean
  inStoreEndlessAisle?: boolean
}

export const HelperText = ({
  siteCode,
  availableForDelivery,
  inStockText,
  inStore,
  inStock,
  isEndlessAisleFromSupplier,
  isEndlessAisleOnHand,
  inStoreEndlessAisle,
  hasSelectedStore,
}: HelperTextProps) => {
  const isEndlessAisle = isEndlessAisleFromSupplier || isEndlessAisleOnHand

  const deliveryStatus = useMemo(() => {
    if (
      !inStock ||
      availableForDelivery.toLowerCase() === 'not available for delivery'
    ) {
      return 'error'
    }

    if (
      availableForDelivery &&
      availableForDelivery.toLowerCase() === 'available for delivery'
    ) {
      return 'success'
    }

    return 'warning'
  }, [inStock, availableForDelivery])

  const isEndlessAisleOnHandWithoutStock =
    isEndlessAisleOnHand && !inStoreEndlessAisle

  const canRenderDeliveryStatus =
    availableForDelivery && !isEndlessAisleFromSupplier
  const canRenderStoreStatus =
    inStockText && (!isEndlessAisle || inStoreEndlessAisle) && hasSelectedStore
  const canRenderEndlessAisleStatus =
    inStockText &&
    (isEndlessAisleFromSupplier || isEndlessAisleOnHandWithoutStock) &&
    hasSelectedStore

  const renderEndlessAisleStatus = (message: string) => {
    if (!canRenderEndlessAisleStatus) {
      return null
    }

    return (
      <Status
        status='market'
        label={
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodySmallExtraLineHeight}
            className='py-0.5 text-primary !pb-0'>
            {message}
          </Typography>
        }
      />
    )
  }

  const renderNoStoreStatus = () => {
    if (hasSelectedStore) {
      return null
    }

    return (
      <Status
        status='error'
        label={
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodySmallExtraLineHeight}
            className={classNames('py-0.5 text-primary !pb-0', {
              'whitespace-nowrap': siteCode === SiteCodes.RTM,
            })}>
            No store selected
          </Typography>
        }
      />
    )
  }

  return (
    <div className='productCard-helperText h-12'>
      {canRenderDeliveryStatus && (
        <Status
          status={deliveryStatus}
          label={
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodySmallExtraLineHeight}
              className='py-0.5 text-primary !pb-0'>
              {availableForDelivery}
            </Typography>
          }
        />
      )}
      {canRenderStoreStatus && (
        <Status
          status={
            inStore
              ? 'success'
              : inStockText === 'Special Order'
              ? 'warning'
              : 'error'
          }
          label={
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodySmallExtraLineHeight}
              className={classNames('py-0.5 text-primary !pb-0', {
                'whitespace-nowrap': siteCode === SiteCodes.RTM,
              })}>
              {inStockText}
            </Typography>
          }
        />
      )}
      {renderEndlessAisleStatus(inStockText)}
      {renderNoStoreStatus()}
    </div>
  )
}
